/**
 * @file interpret/components/useCallApps.ts
 * @brief Which call apps are going on (desktop app), so the Interpreter
 *        offers Call mode only during a call (DES 03-10-2026: "app ko khud
 *        pata hoo kay teams, google yaa zoom khuli hai").
 *
 * @details
 * Polls the desktop app every few seconds while `enabled` (the setup form);
 * during a session it stops and keeps the last answer, so Call mode never
 * flips mid-session. `null` = this app cannot tell (an older desktop app):
 * Call mode is then offered as before.
 *
 * @author DES
 * @date 03-10-2026
 */

import { useEffect, useState } from "react";
import { desktopShell } from "@/shared/desktop/desktopShell";

const POLL_MS = 3000;

export function useCallApps(enabled: boolean): string[] | null {
  const [apps, setApps] = useState<string[] | null>(() =>
    desktopShell()?.activeCallApps ? [] : null,
  );
  useEffect(() => {
    const shell = desktopShell();
    if (!enabled || !shell?.activeCallApps) return;
    let cancelled = false;
    const read = () =>
      void shell
        .activeCallApps?.()
        .then((names) => {
          if (cancelled) return;
          // Same list: keep the same array, so nothing re-renders.
          setApps((prev) =>
            prev && prev.length === names.length && prev.every((n, i) => n === names[i])
              ? prev
              : names,
          );
        })
        .catch(() => undefined);
    read();
    const id = window.setInterval(read, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);
  return apps;
}
